<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="topic-box">
    <div class="topic-intro">您在以下主题领域的表现分析:</div>

    <div class="topic-list">
      <div v-for="(item, index) in userClassifyMastery" :key="item.label + '-' + index" class="topic-item">
        <span class="topic-label">分类:{{ item.label }}</span>
        <div class="bar-shell">
          <div
            class="bar"
            :class="index < 4 ? 'bar-purple' : 'bar-orange'"
            :style="{ '--bar-width': item.percent + '%' }"
          >
            <span class="bar-text"><CountUp :to="item.percent" :duration="1200" suffix="%" /></span>
          </div>
        </div>
      </div>
    </div>

    <div ref="radarChartRef" class="radar-chart"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps } from "vue";
import * as echarts from "echarts";
import CountUp from "./CountUp.vue";

const props = defineProps({
  userClassifyMastery: {
    type: Array,
    default: () => [
      { label: "日常生活", percent: 90 },
      { label: "学校与学习", percent: 85 },
      { label: "人与自然", percent: 72 },
      { label: "社会与文化", percent: 62 },
      { label: "学术与抽象", percent: 45 },
      { label: "专业领域", percent: 20 },
    ],
  },
  classifyMasteryRates: {
    type: Array,
    default: () => [
      { label: "日常生活", percent: 75 },
      { label: "学校与学习", percent: 70 },
      { label: "人与自然", percent: 65 },
      { label: "社会与文化", percent: 55 },
      { label: "学术与抽象", percent: 40 },
      { label: "专业领域", percent: 25 },
    ],
  },
});

const radarChartRef = ref(null);
let chartInstance = null;

const handleResize = () => chartInstance?.resize();

const initChart = () => {
  if (!radarChartRef.value) return;
  if (!props.userClassifyMastery?.length) {
    chartInstance?.dispose();
    chartInstance = null;
    return;
  }
  chartInstance?.dispose();
  chartInstance = echarts.init(radarChartRef.value);
  const option = {
    legend: {
      data: [
        {
          name: "您的掌握程度",
          itemStyle: {
            color: "#f5f2ff",
            borderColor: "#a07dff",
            borderWidth: 1.5,
          },
        },
        {
          name: "同级平均水平",
          itemStyle: {
            color: "#fffaed",
            borderColor: "#ffd352",
            borderWidth: 1.5,
          },
        },
      ],
      top: 0,
      itemWidth: 30,
      itemHeight: 14,
      itemGap: 16,
      textStyle: { fontSize: 15, color: "#333333" },
    },
    radar: {
      indicator: props.userClassifyMastery.map((item, i) => ({
        name: item.label,
        max: 100,
        axisLabel: {
          show: i === 0,
          color: "#666",
          fontSize: 11,
        },
      })),
      radius: "58%",
      center: ["50%", "48%"],
      splitNumber: 5,
      axisName: {
        color: "#333",
        fontSize: 13,
      },
      splitArea: {
        areaStyle: {
          color: ["rgba(160, 125, 255, 0.05)", "rgba(160, 125, 255, 0.1)"],
        },
      },
      splitLine: {
        lineStyle: { color: "rgba(160, 125, 255, 0.3)" },
      },
      axisLine: {
        lineStyle: { color: "rgba(160, 125, 255, 0.4)" },
      },
    },
    series: [
      {
        type: "radar",
        data: [
          {
            value: props.userClassifyMastery.map((item) => item.percent),
            name: "您的掌握程度",
            areaStyle: { color: "rgba(160, 125, 255, 0.35)" },
            lineStyle: { color: "#a07dff", width: 2 },
            itemStyle: { color: "#a07dff" },
            label: { show: false },
          },
          {
            value: (props.classifyMasteryRates || props.userClassifyMastery).map(
              (item) => item.percent
            ),
            name: "同级平均水平",
            areaStyle: { color: "rgba(255, 211, 82, 0.35)" },
            lineStyle: { color: "#ffd352", width: 2 },
            itemStyle: { color: "#ffd352" },
            label: { show: false },
          },
        ],
      },
    ],
  };
  chartInstance.setOption(option);
};

onMounted(() => {
  initChart();
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  chartInstance?.dispose();
});

watch(
  () => [props.userClassifyMastery, props.classifyMasteryRates],
  () => {
    if (chartInstance) initChart();
  },
  { deep: true }
);
</script>

<style scoped lang="scss">
.topic-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .radar-chart {
    width: 100%;
    height: 400px;
    margin-top: 40px;
  }

  .topic-intro {
    font-size: 15px;
    color: #333333;
    font-weight: 500;
    width: 100%;
  }

  .topic-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    margin-top: 15px;
  }

  .topic-item {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    width: 100%;
  }

  .topic-label {
    font-size: 14px;
    color: #333333;
  }

  .bar-shell {
    height: 22px;
    background: #f4f4f4;
    border-radius: 15px;
    overflow: hidden;
  }

  .bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
    min-width: fit-content;
    padding: 0 10px;
    border-radius: 15px;
    width: 0;
    animation: barExpand 1s ease-out forwards;
    transition: width 0.8s ease-out;

    @keyframes barExpand {
      to {
        width: var(--bar-width, 100%);
      }
    }

    .bar-text {
      font-size: 12px;
      color: #ffffff;
      white-space: nowrap;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }

    &.bar-purple {
      background: linear-gradient(90deg, #b398ff 0%, #a786ff 100%);
      box-shadow: inset 0px 0 8px 1px rgba(255, 255, 255, 0.4);
    }

    &.bar-orange {
      background: linear-gradient(90deg, #ffd352 0%, #fbb453 100%);
      box-shadow: inset 0px 0 8px 1px rgba(255, 255, 255, 0.4);
    }
  }
}
</style>
